import React, { useEffect, useState, useMemo, useCallback } from 'react';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import IdeaDetailDialog from '@/components/IdeaDetailDialog';
import VotingButtons from '@/components/VotingButtons';
import ConflictPollCard from '@/components/ConflictPollCard';
import { UserAvatar } from '@/components/UserAvatar';
import { useAuth } from '@/context/AuthContext';
import { api } from '@/lib/api';
import { VOTES_TAB, CONFLICT_POLL } from '@/constants/testIds';
import { categoryMeta } from '@/lib/categories';
import { VOTE_META } from '@/lib/personalities';
import { Trophy, Sparkles, Layers, MessageSquare } from 'lucide-react';

const TIERS = [
    {
        id: 1, label: 'High consensus', testid: VOTES_TAB.tierSectionTier1,
        color: '#4A7C59', tint: 'rgba(74,124,89,0.08)', icon: Trophy,
        desc: 'Everyone or nearly everyone is in.',
    },
    {
        id: 2, label: 'Moderate', testid: VOTES_TAB.tierSectionTier2,
        color: '#C77B3A', tint: 'rgba(199,123,58,0.08)', icon: Sparkles,
        desc: 'Mixed enthusiasm — talk it through.',
    },
    {
        id: 3, label: 'Low interest', testid: VOTES_TAB.tierSectionTier3,
        color: '#8A8175', tint: 'rgba(138,129,117,0.08)', icon: Layers,
        desc: 'Mostly skipped — drop or champion?',
    },
];

function tierFor(idea, participantCount) {
    const { must = 0, nice = 0, skip = 0 } = idea.vote_breakdown || {};
    const total = must + nice + skip;
    if (total === 0) return 3;
    const ratio = must / Math.max(1, participantCount);
    if (ratio >= 0.6) return 1;
    if (idea.priority_score > 0) return 2;
    return 3;
}

function RankedRow({ idea, rank, tripId, onOpen, onVoteChanged }) {
    const cat = categoryMeta(idea.category);
    const Icon = cat.icon;
    const breakdown = idea.vote_breakdown || { must: 0, nice: 0, skip: 0 };
    return (
        <div
            data-testid={VOTES_TAB.rankedItem}
            className="ns-card p-5 flex flex-col sm:flex-row sm:items-center gap-4 cursor-pointer ns-card-hover"
            onClick={() => onOpen?.(idea)}
        >
            <div className="flex items-center gap-4 flex-1 min-w-0">
                <div
                    className="w-12 h-12 rounded-2xl flex items-center justify-center font-display font-semibold text-lg flex-shrink-0"
                    style={{ background: 'var(--ns-soft)', color: 'var(--ns-clay)' }}
                >
                    #{rank}
                </div>
                {idea.thumbnail ? (
                    <img src={idea.thumbnail} alt="" className="w-14 h-14 rounded-xl object-cover flex-shrink-0" />
                ) : (
                    <div className="w-14 h-14 rounded-xl flex items-center justify-center flex-shrink-0" style={{ background: 'var(--ns-soft)' }}>
                        <Icon size={18} style={{ color: cat.color }} />
                    </div>
                )}
                <div className="min-w-0 flex-1">
                    <div className="text-[10px] uppercase tracking-widest font-medium" style={{ color: cat.color }}>{cat.label}</div>
                    <div className="font-display text-lg font-semibold truncate">{idea.location_name}</div>
                    {idea.neighborhood && <div className="text-xs text-[var(--ns-text3)] truncate">{idea.neighborhood}</div>}
                </div>
            </div>
            <div className="flex items-center gap-4 flex-wrap">
                <div className="text-right">
                    <div className="text-[10px] uppercase tracking-widest text-[var(--ns-text3)]">Score</div>
                    <div className="font-display text-2xl font-semibold text-[var(--ns-text)]">{idea.priority_score || 0}</div>
                </div>
                <div className="hidden sm:flex items-center gap-3 text-xs text-[var(--ns-text2)]">
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.must.color }} /> {breakdown.must} must</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.nice.color }} /> {breakdown.nice} nice</span>
                    <span className="inline-flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: VOTE_META.skip.color }} /> {breakdown.skip} skip</span>
                </div>
                {idea.saved_by && (
                    <div className="hidden md:flex items-center gap-1.5 text-xs text-[var(--ns-text3)]">
                        <UserAvatar name={idea.saved_by.name} picture={idea.saved_by.picture} size={20} />
                        {idea.saved_by.name}
                    </div>
                )}
                <VotingButtons
                    ideaId={idea.inspiration_id}
                    tripId={tripId}
                    initialVote={idea.my_vote}
                    breakdown={breakdown}
                    score={idea.priority_score || 0}
                    onVoted={onVoteChanged}
                    compact
                />
            </div>
        </div>
    );
}

export default function VotesTab({ tripId, trip, refreshKey, participantCount = 1 }) {
    const { user } = useAuth();
    const [ideas, setIdeas] = useState([]);
    const [polls, setPolls] = useState([]);
    const [loading, setLoading] = useState(true);
    const [filterTier, setFilterTier] = useState('all');
    const [sort, setSort] = useState('score');
    const [detail, setDetail] = useState(null);

    const load = useCallback(async () => {
        try {
            const [ideasRes, pollsRes] = await Promise.all([
                api.get(`/ideas/trip/${tripId}`),
                api.get(`/trips/${tripId}/polls`),
            ]);
            setIdeas(ideasRes.data?.ideas || []);
            setPolls(pollsRes.data?.polls || []);
        } catch { /* ignore */ }
        finally { setLoading(false); }
    }, [tripId]);

    const updatePoll = (poll) => {
        setPolls((prev) => {
            const filtered = prev.filter((p) => p.poll_id !== poll?.poll_id);
            if (poll) filtered.unshift(poll);
            return filtered;
        });
    };

    useEffect(() => { load(); }, [load, refreshKey]);

    const sorted = useMemo(() => {
        const list = [...ideas];
        if (sort === 'score') list.sort((a, b) => (b.priority_score || 0) - (a.priority_score || 0));
        else if (sort === 'category') list.sort((a, b) => (a.category || '').localeCompare(b.category || ''));
        else if (sort === 'area') list.sort((a, b) => (a.neighborhood || '').localeCompare(b.neighborhood || ''));
        return list;
    }, [ideas, sort]);

    const byTier = useMemo(() => {
        const buckets = { 1: [], 2: [], 3: [] };
        sorted.forEach(i => { buckets[tierFor(i, participantCount)].push(i); });
        return buckets;
    }, [sorted, participantCount]);

    const visibleTiers = TIERS.filter(t => filterTier === 'all' || String(t.id) === filterTier);

    if (loading) return <div className="ns-card p-12 text-center text-[var(--ns-text2)] mt-6">Counting votes…</div>;

    const PollsSection = polls.length > 0 && trip ? (
        <section data-testid={CONFLICT_POLL.votesTabSection} className="mb-8">
            <div className="flex items-center gap-3 mb-4">
                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: 'rgba(29,45,68,0.08)' }}>
                    <MessageSquare size={16} className="text-[var(--ns-navy)]" />
                </span>
                <div>
                    <div className="font-display text-lg font-semibold tracking-tight">Group decisions</div>
                    <div className="text-xs text-[var(--ns-text3)]">Polls created from Group Profile conflicts. Tap an option to vote.</div>
                </div>
                <span className="ml-auto text-xs text-[var(--ns-text3)]">{polls.length} poll{polls.length === 1 ? '' : 's'}</span>
            </div>
            <div className="space-y-3">
                {polls.map((p) => (
                    <div key={p.poll_id} className="ns-card p-5">
                        <div className="font-display font-semibold text-[var(--ns-text)]">{p.conflict_title}</div>
                        {p.conflict_message && (
                            <p className="text-sm text-[var(--ns-text2)] mt-1.5 leading-relaxed">{p.conflict_message}</p>
                        )}
                        <ConflictPollCard
                            trip={trip}
                            conflict={{
                                conflict_id: p.conflict_id,
                                title: p.conflict_title,
                                category: p.conflict_category,
                                severity: p.conflict_severity,
                                message: p.conflict_message,
                            }}
                            poll={p}
                            currentUserId={user?.user_id}
                            onChange={updatePoll}
                        />
                    </div>
                ))}
            </div>
        </section>
    ) : null;

    if (ideas.length === 0) {
        return (
            <div data-testid={VOTES_TAB.container} className="mt-6">
                {PollsSection}
                <div data-testid={VOTES_TAB.empty} className="ns-card p-10 sm:p-14 text-center">
                    <div className="mx-auto w-14 h-14 rounded-2xl flex items-center justify-center mb-4" style={{ background: 'var(--ns-soft)' }}>
                        <Trophy className="text-[var(--ns-clay)]" />
                    </div>
                    <h3 className="font-display text-2xl font-semibold tracking-tight">Nothing to vote on yet</h3>
                    <p className="text-[var(--ns-text2)] mt-2 max-w-md mx-auto leading-relaxed">Save some ideas in the Ideas tab first — votes will rank them here.</p>
                </div>
            </div>
        );
    }

    let runningRank = 0;

    return (
        <div data-testid={VOTES_TAB.container} className="mt-6">
            {PollsSection}
            <div className="flex flex-col sm:flex-row sm:items-center gap-3 mb-6">
                <Select value={filterTier} onValueChange={setFilterTier}>
                    <SelectTrigger data-testid={VOTES_TAB.tierFilter} className="h-10 rounded-full border-[var(--ns-border)] bg-white min-w-[180px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                        <SelectItem value="all">All tiers ({ideas.length})</SelectItem>
                        <SelectItem value="1">Tier 1 · High consensus</SelectItem>
                        <SelectItem value="2">Tier 2 · Moderate</SelectItem>
                        <SelectItem value="3">Tier 3 · Low interest</SelectItem>
                    </SelectContent>
                </Select>
                <Select value={sort} onValueChange={setSort}>
                    <SelectTrigger data-testid={VOTES_TAB.sortSelect} className="h-10 rounded-full border-[var(--ns-border)] bg-white min-w-[160px]">
                        <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="rounded-2xl">
                        <SelectItem value="score">By priority score</SelectItem>
                        <SelectItem value="category">By category</SelectItem>
                        <SelectItem value="area">By area</SelectItem>
                    </SelectContent>
                </Select>
            </div>

            <div className="space-y-8">
                {visibleTiers.map(t => {
                    const items = byTier[t.id] || [];
                    if (items.length === 0) return null;
                    const Icon = t.icon;
                    return (
                        <section key={t.id} data-testid={t.testid}>
                            <div className="flex items-center gap-3 mb-4">
                                <span className="w-9 h-9 rounded-xl flex items-center justify-center" style={{ background: t.tint }}>
                                    <Icon size={16} style={{ color: t.color }} />
                                </span>
                                <div>
                                    <div className="font-display text-lg font-semibold tracking-tight" style={{ color: t.color }}>
                                        Tier {t.id} · {t.label}
                                    </div>
                                    <div className="text-xs text-[var(--ns-text3)]">{t.desc}</div>
                                </div>
                                <span className="ml-auto text-xs text-[var(--ns-text3)]">{items.length} idea{items.length === 1 ? '' : 's'}</span>
                            </div>
                            <div className="space-y-3">
                                {items.map(i => {
                                    runningRank += 1;
                                    return (
                                        <RankedRow
                                            key={i.inspiration_id}
                                            idea={i}
                                            rank={runningRank}
                                            tripId={tripId}
                                            onOpen={setDetail}
                                            onVoteChanged={load}
                                        />
                                    );
                                })}
                            </div>
                        </section>
                    );
                })}
            </div>

            <IdeaDetailDialog
                open={!!detail}
                onOpenChange={(o) => { if (!o) setDetail(null); }}
                idea={detail}
                onVoteChanged={load}
            />
        </div>
    );
}
